import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as apiUtil from '../../util/apiUtil';
import { captureAdAttribution, decodeAdSrc, encodeAdSrc, getAdSrcForSubmit, setSessionAdSrc } from '../../util/adAttribution';

/** 어드민 유입 링크 발급에서 선택 가능한 목적지와 동일하게 유지한다(AdManagementService.targets() 참고). */
export const ALLOWED_TARGET_PATHS = ['/', '/account/login', '/recommend', '/cash/charge', '/selfmessage/create'];
/** 로그인하지 않으면 라우터 가드가 /account/login으로 돌려보내는 목적지. 로그인 성공 후 이 경로로 다시 보낸다. */
const AUTH_REQUIRED_TARGET_PATHS = ['/recommend', '/cash/charge', '/selfmessage/create'];
const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

export const safeDestination = (path: unknown): string =>
  typeof path === 'string' && ALLOWED_TARGET_PATHS.includes(path) ? path : '/';

// 오류 응답에도 랜딩에 계속 머무르지 않게 제한한다.
export const resolveAdLink = (token: string): Promise<{ adSrc: string; targetPath: string }> => new Promise((resolve) => {
  const fallback = { adSrc: encodeAdSrc({ adPath: 'UNKNOWN', v: 2 }), targetPath: '/' };
  const timer = window.setTimeout(() => resolve(fallback), 8000);
  const fail = () => { window.clearTimeout(timer); resolve(fallback); };
  try {
    Promise.resolve(apiUtil.asyncRequest({
    url: '/api/common/ad-link/resolve', method: apiUtil.requestMethod.POST, data: { token },
    success: (result: any) => {
      window.clearTimeout(timer);
      const payload = decodeAdSrc(result?.adSrc);
      resolve(result?.valid === true && payload?.v === 3 && payload.adLinkToken === token
        ? { adSrc: result.adSrc, targetPath: safeDestination(result.targetPath) } : fallback);
    },
    })).catch(fail);
  } catch {
    fail();
  }
});

const AdLandingPage = () => {
  const navigate = useNavigate();
  const hasRun = useRef(false);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    if (!hasRun.current) {
      hasRun.current = true;
      const run = async () => {
        const params = new URLSearchParams(window.location.search);
        let destination = params.get('to') === 'login' ? '/account/login' : '/';
        let adSrc: string;
        if (params.has('link')) {
          const result = await resolveAdLink(params.get('link') || '');
          adSrc = result.adSrc;
          destination = result.targetPath;
        } else {
          captureAdAttribution(window.location.search, { force: true });
          adSrc = getAdSrcForSubmit();
        }
        if (!mounted.current) return;
        setSessionAdSrc(adSrc);
        // 방문 집계는 목적지에서만 수행한다. 여기서는 기존 접속 로그만 유지한다.
        Promise.resolve().then(() => apiUtil.getClientIp()).catch(() => null).then((ip: string | null) => apiUtil.asyncRequest({
          url: '/api/common/adpath', method: apiUtil.requestMethod.POST,
          header: ip ? { 'x-forwarded-for': ip } : {}, data: { adSrc }, success: () => {},
        })).catch(() => {});
        const resolvedDestination = safeDestination(destination);
        // 로그인이 필요한 목적지면, 로그인 전에 라우터 가드가 /account/login으로 돌려보내도
        // 로그인 완료 후 원래 가려던 페이지로 다시 이동할 수 있게 세션에 남겨둔다.
        try {
          if (AUTH_REQUIRED_TARGET_PATHS.includes(resolvedDestination)) {
            window.sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, resolvedDestination);
          }
        } catch (e) {
          // sessionStorage 접근 불가 시 조용히 무시한다(목적지 이동 자체는 계속 진행).
        }
        navigate(resolvedDestination, { replace: true });
      };
      run().catch(() => {
        if (!mounted.current) return;
        setSessionAdSrc(encodeAdSrc({ adPath: 'UNKNOWN', v: 2 }));
        navigate('/', { replace: true });
      });
    }
    return () => { mounted.current = false; };
  }, [navigate]);
  return <div role="status" className="tw-p-6 tw-text-center">페이지로 이동하고 있습니다.</div>;
};

export default AdLandingPage;
